'use client';

import { createContext, ReactNode, useContext, useState } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useInternalNotes } from 'src/shared/queries/internal-notes/useInternalNotes';
import { useCreateInternalNote } from 'src/shared/queries/internal-notes/useInternalNotesMutations';
import { useInternalNotesSocket } from 'src/shared/queries/internal-notes/useInternalNotesSocket';
import { InternalNote } from 'src/shared/types/internalNote.types';

type InternalNotesContextValue = {
  integrationId: string;
  threadId: string;
  currentUserId: string | undefined;
  notes: InternalNote[] | undefined;
  isPending: boolean;
  createMutation: UseMutationResult<InternalNote, Error, string>;
  isComposing: boolean;
  setIsComposing: (composing: boolean) => void;
};

const InternalNotesContext = createContext<InternalNotesContextValue | null>(null);

export function useInternalNotesContext() {
  const context = useContext(InternalNotesContext);
  if (!context) {
    throw new Error('Internal notes compound components must be used within <InternalNotesProvider>');
  }
  return context;
}

type InternalNotesProviderProps = {
  integrationId: string;
  threadId: string;
  children: ReactNode;
  isComposing?: boolean;
  onComposingChange?: (composing: boolean) => void;
};

export function InternalNotesProvider({
  integrationId,
  threadId,
  children,
  isComposing: controlledIsComposing,
  onComposingChange,
}: InternalNotesProviderProps) {
  const { user } = useAuth();
  const { data: notes, isPending } = useInternalNotes(integrationId, threadId);
  const createMutation = useCreateInternalNote(integrationId, threadId);
  const [internalIsComposing, setInternalIsComposing] = useState(false);

  useInternalNotesSocket(integrationId, threadId);

  const isComposing = controlledIsComposing ?? internalIsComposing;
  const setIsComposing = onComposingChange ?? setInternalIsComposing;

  return (
    <InternalNotesContext.Provider
      value={{
        integrationId,
        threadId,
        currentUserId: user?.sub,
        notes,
        isPending,
        createMutation,
        isComposing,
        setIsComposing,
      }}
    >
      {children}
    </InternalNotesContext.Provider>
  );
}
